/**
 * TrophyCase (iter77) — `/badges` route.
 *
 * Two audiences, one page:
 *   · Student: their full earned history with real timestamps + progress on
 *     the locked catalogue.
 *   · Coach / Admin: recent player earnings roll-up sorted newest first.
 *
 * Data:
 *   · GET /students/{id}/achievements/history — student view
 *   · GET /coach/achievements/recent          — coach view
 *
 * Design mirrors the Athlete-Card / Editorial-Progress-Map aesthetic:
 * theme-aware CSS variables, quiet editorial typography, generous
 * whitespace, tier medallions rendered as small SVGs.
 */
import { useEffect, useMemo, useState } from "react";
import { Award, Lock, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSport } from "@/context/SportContext";
import "./TrophyCase.css";

const TIER_COLOR = {
  gold:   "#FFD700",
  silver: "#C0C0C0",
  bronze: "#B87333",
};
const TIER_ORDER = ["gold", "silver", "bronze"];

function TierMedal({ tier = "bronze", earned = true, size = 48 }) {
  const color = TIER_COLOR[tier] || TIER_COLOR.bronze;
  const opacity = earned ? 1 : 0.35;
  return (
    <svg
      width={size} height={size} viewBox="0 0 48 48"
      className="trophy-medal" aria-hidden
      style={{ opacity }}
    >
      {/* Ribbon */}
      <path d="M 14 4 L 20 20 L 28 20 L 34 4 Z" fill={color} opacity="0.85" />
      {/* Medal disc */}
      <circle cx="24" cy="30" r="12" fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      {/* Star */}
      <path
        d="M 24 22 L 26 27 L 31 27 L 27 30 L 29 35 L 24 32 L 19 35 L 21 30 L 17 27 L 22 27 Z"
        fill="rgba(0,0,0,0.55)"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Student view
// ---------------------------------------------------------------------------
function StudentTrophyCase({ studentId, sport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    api.get(`/students/${studentId}/achievements/history`, { params: { sport } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId, sport]);

  const earnedByTier = useMemo(() => {
    if (!data?.earned) return {};
    return data.earned.reduce((acc, b) => {
      const t = b.tier || "bronze";
      (acc[t] = acc[t] || []).push(b);
      return acc;
    }, {});
  }, [data]);

  if (loading) {
    return <div className="trophy-case__loading" data-testid="trophy-case-loading">Loading your trophies…</div>;
  }
  if (!data) return null;

  const { totals = {}, earned = [], locked = [] } = data;

  return (
    <div className="trophy-case" data-testid="trophy-case">
      <header className="trophy-case__hero">
        <Trophy className="trophy-case__hero-icon" strokeWidth={1.5} />
        <div>
          <div className="trophy-case__eyebrow">TROPHY CASE</div>
          <h1 className="trophy-case__title">
            <span className="accent">{totals.earned || 0}</span> {totals.earned === 1 ? "trophy" : "trophies"} in the case.
          </h1>
          <p className="trophy-case__sub">
            Every trophy here is a real milestone. Some come from grinding it out (sessions), some from raw skill
            (level averages), some from finishing what your coach set (milestones).
          </p>
          <div className="trophy-case__totals" data-testid="trophy-case-totals">
            <span className="chip chip--gold">{totals.gold || 0} gold</span>
            <span className="chip chip--silver">{totals.silver || 0} silver</span>
            <span className="chip chip--bronze">{totals.bronze || 0} bronze</span>
            <span className="chip chip--muted">{locked.length} to unlock</span>
          </div>
        </div>
      </header>

      {/* Earned — grouped by tier so the highest-tier trophies get pride of place */}
      {TIER_ORDER.map((tier) => {
        const rows = earnedByTier[tier];
        if (!rows || !rows.length) return null;
        return (
          <section key={tier} className="trophy-case__section" data-testid={`trophy-case-section-${tier}`}>
            <div className="trophy-case__section-head">
              <span className="dot" style={{ background: TIER_COLOR[tier] }} />
              <h2 className="trophy-case__section-title">{tier.toUpperCase()} · {rows.length}</h2>
            </div>
            <ul className="trophy-case__grid">
              {rows.map((b) => (
                <li
                  key={b.id}
                  className="trophy-card trophy-card--earned"
                  data-testid={`trophy-earned-${b.id}`}
                >
                  <TierMedal tier={b.tier} />
                  <div className="trophy-card__body">
                    <div className="trophy-card__name">{b.name}</div>
                    <div className="trophy-card__desc">{b.description}</div>
                    <div className="trophy-card__date">
                      Earned {new Date(b.earned_at).toLocaleDateString("en-GB", {
                        year: "numeric", month: "short", day: "numeric",
                      })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {/* Locked — the next mountain to climb */}
      {locked.length > 0 && (
        <section className="trophy-case__section" data-testid="trophy-case-section-locked">
          <div className="trophy-case__section-head">
            <Lock className="w-3 h-3" />
            <h2 className="trophy-case__section-title">STILL TO UNLOCK · {locked.length}</h2>
          </div>
          <ul className="trophy-case__grid">
            {locked.map((b) => (
              <li key={b.id} className="trophy-card trophy-card--locked" data-testid={`trophy-locked-${b.id}`}>
                <TierMedal tier={b.tier} earned={false} />
                <div className="trophy-card__body">
                  <div className="trophy-card__name">{b.name}</div>
                  <div className="trophy-card__desc">{b.description}</div>
                  <div className="trophy-card__req">Requires: <code>{b.requirement}</code></div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coach view — recent achievements across every booked student.
// ---------------------------------------------------------------------------
function CoachRecentAchievements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/coach/achievements/recent", { params: { limit: 30 } })
      .then((r) => setRows(r.data?.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="trophy-case__loading" data-testid="coach-trophies-loading">Loading recent achievements…</div>;
  }

  return (
    <div className="trophy-case" data-testid="coach-trophy-feed">
      <header className="trophy-case__hero">
        <Award className="trophy-case__hero-icon" strokeWidth={1.5} />
        <div>
          <div className="trophy-case__eyebrow">COACH FEED · RECENT ACHIEVEMENTS</div>
          <h1 className="trophy-case__title">
            <span className="accent">{rows.length}</span> recent {rows.length === 1 ? "trophy" : "trophies"} across your players.
          </h1>
          <p className="trophy-case__sub">
            Newest at the top. Click a player row to jump to their profile and drop a &ldquo;well done&rdquo;.
          </p>
        </div>
      </header>

      {rows.length === 0 ? (
        <div className="trophy-case__empty" data-testid="coach-trophy-empty">
          No achievements yet. Your players will earn their first badges after a session or milestone completion.
        </div>
      ) : (
        <ul className="coach-feed" data-testid="coach-trophy-list">
          {rows.map((r) => (
            <li key={`${r.student_id}-${r.badge_id}`} className="coach-feed__row" data-testid={`coach-trophy-${r.badge_id}-${r.student_id}`}>
              <TierMedal tier={r.tier} size={36} />
              <Link to={`/players/${r.student_id}`} className="coach-feed__name" data-testid={`coach-feed-player-${r.student_id}`}>
                {r.student_name}
              </Link>
              <span className="coach-feed__badge">earned <strong>{r.name}</strong></span>
              <span className="coach-feed__date">
                {new Date(r.first_earned_at).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route entry — routes to Student vs Coach view based on role.
// ---------------------------------------------------------------------------
export default function TrophyCase() {
  const { user } = useAuth();
  const { sport } = useSport();
  if (!user) return null;
  const isCoach = user.role === "coach" || user.role === "admin";
  return isCoach
    ? <CoachRecentAchievements />
    : <StudentTrophyCase studentId={user.id} sport={sport} />;
}
